import React, { useEffect, useMemo, useState } from "react"
import {
  ClipboardList,
  Filter,
  FilterX,
  ShieldCheck,
  Trash,
  TimerReset,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { Button, Col, Container, Nav, NavItem, NavLink, Row } from "reactstrap"

import CandidatesDataTable from "./CandidatesDataTable"
import Spinners from "components/Shared/Spinners"
import Filters from "components/Shared/Filters"

import { showAlert } from "store/reducers/alertReducer"
import { usePermissions } from "hooks"

import { getJobTitlesList } from "store/actions/commonAction"
import { getSignedAndUnSignedCandidates } from "store/actions/contractsAction"
import {
  setSignedAndUnSignedPayload,
  toggleFilterVisibility,
  resetPayload,
} from "store/reducers/contractReducer"

const navItems = [
  {
    id: 0,
    name: "All",
  },
  {
    id: 2,
    name: "Signed",
  },
  {
    id: 1,
    name: "Not Signed",
  },
  {
    id: 3,
    name: "Rejected",
  },
]

const ContractParticipants = () => {
  const {
    loading,
    candidates,
    totalPages,
    totalCount,
    currentPage,
    isShowFilter,
    signedAndUnSignedPayload,
  } = useSelector(state => state.Contract)
  const navigate = useNavigate()
  const {
    t,
    i18n: { language },
  } = useTranslation()
  document.title = t("Participants | Yamama")
  const { showAll, permissions } = usePermissions("contract")

  const dispatch = useDispatch()

  const { search } = useLocation()
  const { jobTitlesList } = useSelector(state => state.Common)

  const uuid = new URLSearchParams(search).get("uuid")

  const [activeTab, setActiveTab] = useState(0)
  const [trigger, setTrigger] = useState(false)

  useEffect(() => {
    getAllData()
  }, [activeTab, trigger])

  useEffect(() => {
    dispatch(getJobTitlesList())
  }, [])

  const formattedJobTitles = useMemo(() => {
    if (!jobTitlesList) return []

    return jobTitlesList.map(jobTitle => ({
      value: jobTitle.id,
      label: jobTitle[language === "en" ? "name" : "name_ar"],
    }))
  }, [jobTitlesList])

  const getAllData = page => {
    let data = {
      ...signedAndUnSignedPayload,
      filters: {
        ...signedAndUnSignedPayload.filters,
        type: activeTab === 0 ? null : activeTab,
        contract_batch_uuid: uuid,
      },
    }
    dispatch(getSignedAndUnSignedCandidates(data))
  }

  const onPageChange = page => {
    dispatch(setSignedAndUnSignedPayload({ page_number: page }))
    setTrigger(!trigger)
  }

  const toggleTab = tab => {
    if (activeTab !== tab) {
      setActiveTab(tab)
      dispatch(resetPayload())
    }
  }

  const filters = {
    keyword: {
      type: "text",
      placeholder: t("Search by name"),
      label: t("Search By"),
      colSize: 4,
    },
    is_saudi: {
      type: "select",
      placeholder: t("Select Nationality"),
      label: t("Select Nationality"),
      options: [
        { value: "saudi", label: t("Saudi") },
        { value: "non_saudi", label: t("Non Saudi") },
      ],
      colSize: 4,
    },
    job_title_ids: {
      type: "select",
      placeholder: t("Select Job Title"),
      label: t("Select Job Title"),
      options: formattedJobTitles,
      colSize: 4,
      multiple: true,
    },
    date_range: {
      type: "dateRange",
      label: t("Select Date"),
      colSize: 4,
    },
  }

  const handleReset = () => {
    dispatch(resetPayload())
    setTrigger(!trigger)
  }

  if (loading) return <Spinners />

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="12">
              <div className="d-flex justify-content-between mb-4 align-items-center">
                <h4 className="card-title ">
                  <i
                    className="bx bx-arrow-back me-1 fs-5 cursor-pointer"
                    onClick={() => navigate(`/contracts`)}
                  ></i>
                  {t("Contract Candidates")}
                </h4>
                <div className="d-flex gap-3">
                  {/* {activeTab === 2 && show_enable_assessment_for_all && ( */}

                  {/* )} */}

                  {/* <button
                      className="main_btn fit_content"
                      onClick={handleExportXLSX}
                      disabled={isExporting}
                    >
                      {t("Download XLSX")}
                    </button> */}
                  <Button
                    color="primary"
                    onClick={() => dispatch(toggleFilterVisibility())}
                    title={t("Show/Hide Filter")}
                  >
                    {isShowFilter ? <FilterX /> : <Filter />}
                  </Button>
                </div>
              </div>

              <Nav tabs className="one_line_parent mb-3">
                {navItems.map(nav => {
                  return (
                    <NavItem key={nav.id}>
                      <NavLink
                        className={activeTab === nav.id ? "active" : ""}
                        onClick={() => toggleTab(nav.id)}
                      >
                        {t(nav.name)}
                      </NavLink>
                    </NavItem>
                  )
                })}
              </Nav>
              {isShowFilter && (
                <Col xs="12">
                  <Filters
                    filters={filters}
                    state={signedAndUnSignedPayload.filters}
                    setState={data => {
                      dispatch(
                        setSignedAndUnSignedPayload({
                          filters: {
                            ...signedAndUnSignedPayload.filters,
                            ...data,
                          },
                        })
                      )
                    }}
                    handleSearch={() => setTrigger(!trigger)}
                    handleReset={handleReset}
                  />
                </Col>
              )}
              <CandidatesDataTable
                data={candidates}
                loading={loading}
                currentPage={currentPage}
                onPageChange={onPageChange}
                totalPages={totalPages}
                totalCount={totalCount}
                activeTab={
                  navItems.find(i => i.id === activeTab)?.name.toLowerCase() ||
                  ""
                }
                payload={signedAndUnSignedPayload}
                trigger={() => setTrigger(!trigger)}
                showAll={showAll}
                permissions={permissions}
              />
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default ContractParticipants
